import React, { useState, useEffect, useRef } from 'react';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../../services/firebase';
import haukeApiService from '../../services/haukeApi';
import { showSuccessToast, showErrorToast } from '../../utils/toastUtils';
import '../../assets/css/profil-admin.css';

const Bilder = ({ hofId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [dragActive, setDragActive] = useState(false);
  
  // Drag & Drop State für Bildumordnung
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  
  const fileInputRef = useRef(null);

  useEffect(() => {
    loadData();
  }, [hofId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const response = await haukeApiService.getDirekterzeugerDetails(hofId);
      setData(response.data);
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };

  

  const handleImageUpload = async (files) => {
    if (!files || files.length === 0) return;

    try {
      setUploading(true);
      const uploadPromises = Array.from(files).map(async (file, index) => {
        // Validierung
        if (!file.type.startsWith('image/')) {
          throw new Error(`Datei ${file.name} ist kein Bild`);
        }
        
        if (file.size > 10 * 1024 * 1024) { // 10MB Limit
          throw new Error(`Datei ${file.name} ist zu groß (max. 10MB)`);
        }

        const timestamp = Date.now();
        const fileName = `${timestamp}_${index}_${file.name}`;
                    const imageRef = ref(storage, `producer/${hofId}/general/${fileName}`);
        
        setUploadProgress(prev => ({ ...prev, [file.name]: 0 }));
        
        // Upload mit Progress
        await uploadBytes(imageRef, file);
        setUploadProgress(prev => ({ ...prev, [file.name]: 100 }));
        
        // Download URL holen
        const downloadURL = await getDownloadURL(imageRef);
        return downloadURL;
      });

      const uploadedUrls = await Promise.all(uploadPromises);
      
      // Neue URLs zur bestehenden Liste hinzufügen
      const currentImages = data.imageUrl || [];
      const newImageUrls = [...currentImages, ...uploadedUrls];
      
      // In Datenbank speichern
      await haukeApiService.updateDirekterzeuger(hofId, { imageUrl: newImageUrls });
      
      // Lokalen State aktualisieren
      setData({ ...data, imageUrl: newImageUrls });
      
      showSuccessToast(`${uploadedUrls.length} Bild(er) erfolgreich hochgeladen!`);
      
    } catch (error) {
      console.error('Error uploading images:', error);
      setError(`Fehler beim Hochladen: ${error.message}`);
    } finally {
      setUploading(false);
      setUploadProgress({});
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleFileSelect = (e) => {
    const files = e.target.files;
    handleImageUpload(files);
  };

  const handleDeleteImage = async (index) => {
    if (!window.confirm('Bild wirklich löschen?')) return;

    try {
      setSaving(true);
      const newImageUrls = [...data.imageUrl];
      newImageUrls.splice(index, 1);
      
      await haukeApiService.updateDirekterzeuger(hofId, { imageUrl: newImageUrls });
      setData({ ...data, imageUrl: newImageUrls });
      
      showSuccessToast('Bild erfolgreich gelöscht!');
    } catch (err) {
      console.error('Error deleting image:', err);
      setError('Fehler beim Löschen: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  // Drag & Drop Handler für Bildumordnung
  const handleImageDragStart = (e, index) => {
    console.log('Drag start:', index);
    setDraggedItem({ index, url: data.imageUrl[index] });
    e.dataTransfer.effectAllowed = 'move';
    
    // Besseres visuelles Feedback
    setTimeout(() => {
      e.target.style.opacity = '0.5';
    }, 0);
  };

  const handleImageDragEnd = (e) => {
    console.log('Drag end');
    e.target.style.opacity = '1';
    setDraggedItem(null);
    setDragOverIndex(null);
  };

  const handleImageDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleImageDragEnter = (e, index) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverIndex(index);
  };

  const handleImageDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleImageDrop = async (e, toIndex) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!draggedItem || draggedItem.index === toIndex) {
      setDragOverIndex(null);
      return;
    }

    await handleReorderImages(draggedItem.index, toIndex);
    setDragOverIndex(null);
  };

  const handleReorderImages = async (fromIndex, toIndex) => {
    if (fromIndex === toIndex) return;
    
    try {
      setSaving(true);
      const currentImages = [...(data.imageUrl || [])];
      
      // Array umordnen
      const [movedItem] = currentImages.splice(fromIndex, 1);
      currentImages.splice(toIndex, 0, movedItem);
      
      console.log('Reordering:', { fromIndex, toIndex, newOrder: currentImages });
      
      // In Datenbank speichern
      await haukeApiService.updateDirekterzeuger(hofId, { 
        imageUrl: currentImages,
        lastEdited: Date.now()
      });
      
      // Lokalen State aktualisieren
      setData({ ...data, imageUrl: currentImages });
      
      showSuccessToast('Bildreihenfolge erfolgreich geändert!');
    } catch (error) {
      console.error('Fehler beim Umordnen der Bilder:', error);
      setError('Fehler beim Umordnen der Bilder: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  // File Upload Drag & Drop Handler
  const handleFileDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };

  const handleFileDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const handleFileDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleFileDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const files = e.dataTransfer.files;
    handleImageUpload(files);
  };

  if (loading) return <div className="webling-loading">Daten werden geladen...</div>;
  if (error) return <div className="webling-error">{error}</div>;
  if (!data) return <div className="webling-error">Keine Daten verfügbar</div>;

  return (
    <div className="webling-container">
      <div className="webling-content">
        <div className="form-section">
          <h3>Bildergalerie</h3>
          
          {/* Upload Area */}
          <div 
            className={`image-upload-area ${dragActive ? 'drag-active' : ''} ${uploading ? 'uploading' : ''}`}
            onDragEnter={handleFileDragEnter}
            onDragLeave={handleFileDragLeave}
            onDragOver={handleFileDragOver}
            onDrop={handleFileDrop}
            onClick={() => !uploading && fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*"
              onChange={handleFileSelect}
              disabled={uploading}
              style={{ display: 'none' }}
            />
            
            <div className="upload-content">
              {uploading ? (
                <div className="upload-status">
                  <i className="material-icons spinning">hourglass_empty</i>
                  <h4>Bilder werden hochgeladen...</h4>
                  {Object.entries(uploadProgress).map(([fileName, progress]) => (
                    <div key={fileName} className="upload-progress">
                      <span className="file-name">{fileName}</span>
                      <div className="progress-bar">
                        <div className="progress-fill" style={{ width: `${progress}%` }}></div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <>
                  <i className="material-icons">cloud_upload</i>
                  <h4>Bilder hochladen</h4>
                  <p>Klicken Sie hier oder ziehen Sie Bilder in diesen Bereich</p>
                  <small>Unterstützt: JPG, PNG, WEBP (max. 10MB pro Bild)</small>
                </>
              )}
            </div>
          </div>
          
          {/* Bildergalerie */}
          <div className="image-section">
            {data.imageUrl && data.imageUrl.length > 0 ? (
              <div className="gallery-grid">
                {data.imageUrl.map((url, index) => (
                  <div
                    key={index}
                    className={`gallery-item ${draggedItem?.index === index ? 'dragging' : ''} ${dragOverIndex === index ? 'drag-over' : ''}`}
                    draggable={true}
                    onDragStart={(e) => handleImageDragStart(e, index)}
                    onDragEnd={handleImageDragEnd}
                    onDragOver={handleImageDragOver}
                    onDragEnter={(e) => handleImageDragEnter(e, index)}
                    onDragLeave={handleImageDragLeave}
                    onDrop={(e) => handleImageDrop(e, index)}
                  >
                    <img
                      src={url}
                      alt={`Bild ${index + 1}`}
                      onDragStart={(e) => e.preventDefault()} // Prevent default drag behavior on image itself
                    />
                    <div className="gallery-overlay">
                      <div className="gallery-actions">
                        <button 
                          onClick={() => window.open(url, '_blank')}
                          className="view-btn"
                          title="Bild ansehen"
                        >
                          <i className="material-icons">visibility</i>
                        </button>
                        <button 
                          onClick={() => handleDeleteImage(index)}
                          className="delete-btn"
                          disabled={saving}
                          title="Bild löschen"
                        >
                          <i className="material-icons">delete</i>
                        </button>
                      </div>
                    </div>
                    {index === 0 && (
                      <div className="main-image-badge">
                        <i className="material-icons">star</i>
                        <span>Hauptbild</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="no-images">
                <div className="empty-state">
                  <i className="material-icons">photo_library</i>
                  <h3>Keine Bilder vorhanden</h3>
                  <p>Noch keine Bilder für diesen Direkterzeuger hochgeladen.</p>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="form-section">
          <h3>Bild-Informationen</h3>
          <table className="webling-form">
            <tbody>
              <tr className="form-row">
                <td className="form-label">Anzahl Bilder</td>
                <td className="form-input">
                  <div className="readonly-display">
                    {data.imageUrl ? data.imageUrl.length : 0}
                  </div>
                </td>
              </tr>
              <tr className="form-row">
                <td className="form-label">Erstes Bild (Hauptbild)</td>
                <td className="form-input">
                  <div className="readonly-display">
                    {data.imageUrl && data.imageUrl.length > 0 ? 'Vorhanden' : 'Nicht vorhanden'}
                  </div>
                </td>
              </tr>
              <tr className="form-row">
                <td className="form-label">Letzte Aktualisierung</td>
                <td className="form-input">
                  <div className="readonly-display">
                    {data.lastEdited ? new Date(data.lastEdited).toLocaleString('de-DE') : 'Nicht verfügbar'}
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="webling-footer">
        <button 
          onClick={() => fileInputRef.current?.click()}
          className="save-button"
          disabled={uploading || saving}
        >
          <i className="material-icons">add_photo_alternate</i>
          {uploading ? 'Hochladen...' : 'Weitere Bilder hinzufügen'}
        </button>
        <button 
          onClick={() => window.history.back()}
          disabled={uploading || saving}
          className="cancel-button"
        >
          Schließen
        </button>
      </div>
    </div>
  );
};

export default Bilder; 